import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, Shield, Eye, BarChart3, Award, Lock, MapPin, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

export default function Landing() {
    const [stats, setStats] = useState(null);
    const [contests, setContests] = useState([]);

    useEffect(() => {
        api.get("/analytics/overview").then((r) => setStats(r.data)).catch(() => {});
        api.get("/contests").then((r) => setContests(r.data.contests || [])).catch(() => {});
    }, []);

    return (
        <main className="pt-16">
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#FDFBF6] to-[var(--bg)]">
                <div className="absolute inset-0 bg-soft-grid opacity-60" />
                <div className="absolute top-20 right-10 w-80 h-80 rounded-full bg-[var(--gold)]/10 blur-3xl ai-orb" />
                <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-[var(--navy)]/8 blur-3xl" />

                <div className="container-x relative py-24 lg:py-28">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7 space-y-7 fade-up">
                            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white border border-[var(--gold)]/40 shadow-sm">
                                <img src="/logo-cucdanso.png" alt="Cục Dân số" className="w-6 h-6 rounded-full" />
                                <span className="text-xs font-bold text-[var(--navy)] tracking-wide">CỤC DÂN SỐ · BỘ Y TẾ VIỆT NAM</span>
                            </div>
                            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
                                Cuộc thi <span className="relative">
                                    <span className="text-[var(--gold-deep)]">trực tuyến</span>
                                    <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none">
                                        <path d="M0 5 Q 50 0, 100 5 T 200 5" stroke="var(--gold)" strokeWidth="3" fill="none" />
                                    </svg>
                                </span><br />
                                tuyên truyền <span className="text-[var(--navy)]">Dân số &amp; Phát triển</span>
                            </h1>
                            <p className="text-slate-700 text-lg max-w-2xl leading-relaxed">
                                Sân chơi trực tuyến toàn quốc dành cho mọi người dân Việt Nam — tìm hiểu Luật Dân số,
                                chính sách dân số, sức khoẻ sinh sản và phát triển bền vững. Cùng nhau lan toả thông điệp
                                <strong className="text-[var(--navy)]"> "Mỗi gia đình hạnh phúc — Một Việt Nam thịnh vượng"</strong>.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/cuoc-thi" className="btn-gold" data-testid="cta-join-contest">
                                    Tham gia cuộc thi <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/thu-vien" className="btn-secondary" data-testid="cta-library">
                                    Tra cứu văn bản pháp luật
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5 fade-up delay-2">
                            <div className="relative">
                                <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-[var(--gold)]/20 to-[var(--navy)]/10 blur-3xl" />
                                <div className="relative card p-8 bg-white text-center">
                                    <img src="/logo-cucdanso.png" alt="Cục Dân số - Bộ Y tế"
                                        className="w-44 h-44 mx-auto mb-5 drop-shadow-lg" />
                                    <div className="font-heading font-bold text-2xl text-[var(--navy)] leading-tight">
                                        Dân số &amp; Phát triển
                                    </div>
                                    <div className="text-sm text-[var(--gold-deep)] font-semibold mt-1">
                                        Vì một Việt Nam thịnh vượng
                                    </div>
                                    <div className="grid grid-cols-4 gap-2 mt-6 pt-5 border-t border-[var(--border)]">
                                        <StatBox label="Cuộc thi" value={stats?.total_contests ?? "—"} compact />
                                        <StatBox label="Thí sinh" value={stats?.total_users ?? "—"} compact />
                                        <StatBox label="Bài thi" value={stats?.total_submissions ?? "—"} compact />
                                        <StatBox label="Tỉnh" value="63" compact />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES BENTO */}
            <section className="section relative">
                <div className="container-x">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold mb-3">Nền tảng GovTech</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold inline-block gold-underline">Sáu trụ cột kiến trúc</h2>
                        <p className="text-slate-600 mt-7">Một nền tảng — đa cuộc thi — AI hỗ trợ — Con người quyết định cuối.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        <FeatureCard icon={<Brain />} title="AI Chấm thi sơ bộ"
                            desc="Đánh giá tự luận theo rubric 4 tiêu chí, sinh giải trình, độ tin cậy. BGK có quyền phủ quyết." />
                        <FeatureCard icon={<Shield />} title="Identity & Anti-Fraud"
                            desc="Đăng nhập đa kênh, risk scoring tài khoản, phát hiện vote bất thường theo IP và tốc độ." />
                        <FeatureCard icon={<Eye />} title="Governance Layer"
                            desc="Audit log bất biến cho mọi hành động quan trọng. Hỗ trợ khiếu nại và override." />
                        <FeatureCard icon={<BarChart3 />} title="Analytics Realtime"
                            desc="Dashboard theo tỉnh thành, độ tuổi, giới tính. Export báo cáo Excel/PDF." />
                        <FeatureCard icon={<Award />} title="Bảng xếp hạng"
                            desc="Kết hợp điểm trắc nghiệm, AI tự luận, BGK và bình chọn cộng đồng (có trọng số)." />
                        <FeatureCard icon={<Lock />} title="Bảo mật & Riêng tư"
                            desc="Mã hoá PII, RBAC nhiều cấp, retention rõ ràng cho dữ liệu CCCD/PII." />
                    </div>
                </div>
            </section>

            {/* ACTIVE CONTESTS */}
            <section className="section bg-section relative">
                <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none" />
                <div className="container-x relative">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold mb-2">Đang mở</div>
                            <h2 className="font-heading text-3xl sm:text-4xl font-bold gold-underline inline-block">Cuộc thi nổi bật</h2>
                        </div>
                        <Link to="/cuoc-thi" className="btn-secondary !py-2" data-testid="see-all-contests">
                            Xem tất cả <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {contests.slice(0, 4).map((c) => (
                            <Link key={c.id} to={`/cuoc-thi/${c.id}`} className="card card-hover overflow-hidden block group" data-testid={`contest-card-${c.id}`}>
                                <div className="relative h-44 overflow-hidden bg-[var(--navy)]">
                                    {c.cover_image && <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />}
                                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/80 via-transparent to-transparent" />
                                    <div className="absolute top-3 right-3 badge badge-gold">{statusLabel(c.status)}</div>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-heading text-lg font-bold mb-2 text-[var(--navy)] group-hover:text-[var(--gold-deep)] transition">{c.title}</h3>
                                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">{c.description}</p>
                                    <div className="flex items-center justify-between text-xs text-slate-500">
                                        <span>{c.organizer}</span>
                                        <span className="text-[var(--gold-deep)] font-semibold">{c.total_participants ?? 0} thí sinh →</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="section">
                <div className="container-x">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold mb-3">Quy trình</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold gold-underline inline-block">Tham gia chỉ 5 bước</h2>
                    </div>
                    <div className="grid md:grid-cols-5 gap-4 relative">
                        <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[var(--gold)] via-[var(--gold)] to-[var(--gold)] opacity-30" />
                        {[
                            { n: "01", t: "Đăng ký", d: "Google / Facebook / Email" },
                            { n: "02", t: "Khai báo", d: "CCCD · SĐT · Tỉnh thành" },
                            { n: "03", t: "Làm bài", d: "Trắc nghiệm + Tự luận" },
                            { n: "04", t: "AI chấm sơ bộ", d: "Sinh feedback, độ tin cậy" },
                            { n: "05", t: "BGK duyệt", d: "Công bố kết quả chính thức" },
                        ].map((s, i) => (
                            <div key={i} className="card p-5 relative z-10">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--gold-soft)] flex items-center justify-center text-[var(--navy)] font-bold text-sm mb-3">{s.n}</div>
                                <div className="font-heading font-bold text-[var(--navy)] mb-1">{s.t}</div>
                                <div className="text-xs text-slate-600">{s.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GOVERNANCE TRUST */}
            <section className="section">
                <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-5">
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold">Nguyên tắc cốt lõi</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight text-[var(--navy)]">
                            AI <span className="text-[var(--gold-deep)]">hỗ trợ</span>,<br /> Con người <span className="text-[var(--gold-deep)]">quyết định</span>
                        </h2>
                        <ul className="space-y-4 text-slate-700">
                            {[
                                "AI không tự công bố người chiến thắng",
                                "Mọi quyết định trừng phạt đều cần con người duyệt",
                                "Vote chỉ là yếu tố bổ trợ, không phải tiêu chí duy nhất",
                                "Audit log không thể chỉnh sửa, sẵn sàng xuất khi cần",
                            ].map((t, i) => (
                                <li key={i} className="flex gap-3 items-start">
                                    <CheckCircle2 className="w-5 h-5 text-[var(--gold-deep)] mt-0.5 flex-shrink-0" />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="card-dark p-8 relative overflow-hidden bg-gradient-to-br from-[var(--navy)] to-[var(--navy-soft)]">
                        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[var(--gold)]/20 blur-3xl" />
                        <div className="absolute top-6 right-6 flex gap-1">
                            <Star className="w-4 h-4 fill-[var(--gold)] text-[var(--gold)]" />
                            <Star className="w-4 h-4 fill-[var(--gold)] text-[var(--gold)]" />
                            <Star className="w-4 h-4 fill-[var(--gold)] text-[var(--gold)]" />
                        </div>
                        <div className="relative space-y-4">
                            <div className="flex items-center gap-3 mb-4">
                                <MapPin className="w-5 h-5 text-[var(--gold)]" />
                                <span className="text-xs uppercase tracking-widest text-[var(--gold)] font-semibold">Phạm vi quốc gia</span>
                            </div>
                            <div className="text-6xl font-heading font-bold !text-[var(--navy)]">63<span className="text-[var(--gold)]">/63</span></div>
                            <div className="text-slate-600">Tỉnh, thành phố trên cả nước</div>
                            <div className="border-t border-[var(--border)] pt-5 grid grid-cols-2 gap-4 text-xs">
                                <DarkBox title="Hạ tầng" value="Đám mây" />
                                <DarkBox title="Mã hoá" value="TLS · AES-256" />
                                <DarkBox title="Đăng nhập" value="3 kênh" />
                                <DarkBox title="AI providers" value="3 nhà cung cấp" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

function StatBox({ label, value, compact }) {
    if (compact) return (
        <div className="text-center">
            <div className="text-xl font-heading font-bold text-[var(--navy)]">{value}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">{label}</div>
        </div>
    );
    return (
        <div className="stat-card">
            <div className="text-2xl font-heading font-bold text-[var(--navy)]">{value}</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{label}</div>
        </div>
    );
}

function AIRow({ name, pct, primary }) {
    const color = primary ? "var(--gold)" : "var(--navy)";
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-700 font-medium">{name}</span>
                <span className="font-semibold" style={{ color }}>{pct}%</span>
            </div>
            <div className="h-1.5 bg-[var(--bg-alt)] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

function Box({ title, value }) {
    return (
        <div>
            <div className="text-slate-500 uppercase tracking-widest text-[10px]">{title}</div>
            <div className="text-[var(--navy)] font-bold mt-0.5">{value}</div>
        </div>
    );
}

function DarkBox({ title, value }) {
    return (
        <div>
            <div className="text-[var(--gold)] uppercase tracking-widest text-[10px] font-semibold">{title}</div>
            <div className="text-[var(--navy)] font-bold mt-0.5">{value}</div>
        </div>
    );
}

function FeatureCard({ icon, title, desc }) {
    return (
        <div className="card card-hover p-7">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--gold)] to-[var(--gold-soft)] flex items-center justify-center mb-5 text-[var(--navy)]">
                {icon}
            </div>
            <h3 className="font-heading font-bold text-lg mb-2 text-[var(--navy)]">{title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
        </div>
    );
}

function statusLabel(s) {
    const map = { draft: "Nháp", published: "Công bố", active: "Đang mở", reviewing: "Đang chấm", finalized: "Đã kết thúc", archived: "Lưu trữ" };
    return map[s] || s;
}
